import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { PaperPlaneRight, SignOut, ArrowClockwise, Trophy, Copy } from '@phosphor-icons/react';
import { TicTacToeBoard } from './TicTacToeBoard';
import { CheckersBoard } from './CheckersBoard';
import { PlayersStatus } from './PlayersStatus';
import { WatchParty } from './WatchParty';
import { ChatTools } from '../chat/ChatTools';
import { getToken } from '@/utils/auth';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export const GameRoom = ({ room: initialRoom, user, onLeaveRoom, onGameEnd }) => {
  const [room, setRoom] = useState(initialRoom);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isDictating, setIsDictating] = useState(false);
  const [themedEmojis, setThemedEmojis] = useState([]);
  const [watchParty, setWatchParty] = useState(initialRoom.watch_party || null);
  const wsRef = useRef(null);
  const scrollRef = useRef(null);
  const gameEndNotifiedRef = useRef(false);

  const myPlayer = room.players.find(p => p.user_id === user.id);
  const mySymbol = myPlayer?.symbol;

  // Prefer the embedded snapshot from the room doc (populated by /rooms/create),
  // fall back to the dedicated endpoint. Never call RAWG directly from the browser.
  useEffect(() => {
    if (room.game_metadata?.themed_emojis) {
      setThemedEmojis(room.game_metadata.themed_emojis);
      return;
    }
    let cancelled = false;
    const API = `${BACKEND_URL}/api`;
    axios
      .get(`${API}/rooms/${room.code}/metadata`)
      .then((res) => {
        if (!cancelled) setThemedEmojis(res.data.themed_emojis || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [room.code, room.game_metadata]);

  useEffect(() => {
    // Cookie-based auth: browser auto-sends dashlink_token cookie to same-origin WS
    const ws = new WebSocket(`${WS_URL}/api/ws/room/${room.code}`);
    wsRef.current = ws;

    ws.onopen = () => logger.log('Room WebSocket connected');
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === 'room_state' || msg.type === 'game_update') {
        setRoom(msg.room);
        // Notify on game end (once)
        if (msg.room.state.game_over && !gameEndNotifiedRef.current) {
          gameEndNotifiedRef.current = true;
          if (msg.room.state.is_draw) {
            toast.info('Draw! Both players get +25 points');
          } else {
            const winnerPlayer = msg.room.players.find(p => p.symbol === msg.room.state.winner);
            if (winnerPlayer?.user_id === user.id) {
              toast.success('🏆 You won! +100 points');
            } else {
              toast.error(`${winnerPlayer?.username || 'Opponent'} won this round`);
            }
          }
          onGameEnd?.();
        }
        if (!msg.room.state.game_over) {
          gameEndNotifiedRef.current = false;
        }
      } else if (msg.type === 'chat') {
        setMessages(prev => [...prev, msg]);
      } else if (msg.type === 'watch_party_updated') {
        setWatchParty(msg.watch_party);
        setRoom(prev => ({ ...prev, watch_party: msg.watch_party }));
      } else if (msg.type === 'error') {
        toast.error(msg.message);
      }
    };
    ws.onerror = (err) => logger.error('Room WS error:', err);
    ws.onclose = () => logger.log('Room WebSocket disconnected');

    return () => {
      if (ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [room.code, user.id, onGameEnd]);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current;
      if (el.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages]);

  const sendChat = () => {
    if (!inputMessage.trim() || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'chat', content: inputMessage.trim() }));
    setInputMessage('');
  };

  const makeMove = useCallback((...args) => {
    if (!wsRef.current) return;
    if (room.game_type === 'tic-tac-toe') {
      wsRef.current.send(JSON.stringify({ type: 'move', position: args[0] }));
    } else {
      wsRef.current.send(JSON.stringify({ type: 'move', from: args[0], to: args[1] }));
    }
  }, [room.game_type]);

  const resetGame = () => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'reset' }));
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(room.code);
    toast.success('Room code copied!');
  };

  const state = room.state;
  const waitingForPlayer = room.players.length < 2;

  return (
    <div className="flex-1 flex bg-white" data-testid="game-room">
      {/* Left: Game board area */}
      <div className="flex-1 flex flex-col border-r border-[#E4E4E7]">
        <div className="p-4 border-b border-[#E4E4E7] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-[#09090B]">
              {room.game_type === 'tic-tac-toe' ? 'Tic-Tac-Toe' : 'Checkers'}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-[#71717A]">Room:</span>
              <button 
                onClick={copyCode}
                className="text-sm font-mono font-bold text-[#0047FF] hover:underline flex items-center gap-1"
                data-testid="copy-code-button"
              >
                {room.code}
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div className="flex gap-2">
            {state.game_over && (
              <Button onClick={resetGame} size="sm" className="bg-[#0047FF] hover:bg-[#0037CC] text-white" data-testid="reset-game-button">
                <ArrowClockwise size={16} className="mr-1" />
                New Game
              </Button>
            )}
            <Button onClick={onLeaveRoom} variant="outline" size="sm" className="border-[#E4E4E7]" data-testid="leave-room-button">
              <SignOut size={16} className="mr-1" />
              Leave
            </Button>
          </div>
        </div>

        {waitingForPlayer ? (
          <div className="flex-1 flex items-center justify-center bg-[#FAFAFA]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0047FF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="font-heading text-xl font-bold text-[#09090B] mb-2">Waiting for opponent...</h3>
              <p className="text-[#71717A] mb-4">Share this code with a friend:</p>
              <button 
                onClick={copyCode}
                className="text-3xl font-mono font-bold text-[#0047FF] tracking-widest hover:underline"
              >
                {room.code}
              </button>
            </div>
          </div>
        ) : state.game_over ? (
          <div className="flex-1 flex flex-col">
            <div className="p-6 bg-[#FAFAFA] border-b border-[#E4E4E7] text-center">
              <Trophy size={48} weight="fill" className="mx-auto text-[#F59E0B] mb-2" />
              {state.is_draw ? (
                <h3 className="font-heading text-2xl font-bold text-[#09090B]">It&apos;s a Draw!</h3>
              ) : (
                <h3 className="font-heading text-2xl font-bold text-[#09090B]">
                  {room.players.find(p => p.symbol === state.winner)?.username} Wins!
                </h3>
              )}
              <p className="text-sm text-[#71717A] mt-1">
                {state.is_draw ? '+25 points each' : '+100 points to winner'}
              </p>
            </div>
            <div className="flex-1 flex items-center justify-center">
              {room.game_type === 'tic-tac-toe' ? (
                <TicTacToeBoard state={state} mySymbol={mySymbol} currentTurn={state.current_turn} onMove={makeMove} gameOver={true} />
              ) : (
                <CheckersBoard state={state} mySymbol={mySymbol} currentTurn={state.current_turn} onMove={makeMove} gameOver={true} />
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            {room.game_type === 'tic-tac-toe' ? (
              <TicTacToeBoard state={state} mySymbol={mySymbol} currentTurn={state.current_turn} onMove={makeMove} gameOver={false} />
            ) : (
              <CheckersBoard state={state} mySymbol={mySymbol} currentTurn={state.current_turn} onMove={makeMove} gameOver={false} />
            )}
          </div>
        )}
      </div>

      {/* Right: Players + Watch Party + Chat */}
      <div className="w-80 flex flex-col bg-white">
        <PlayersStatus players={room.players} roomCode={room.code} />

        <WatchParty room={room} watchParty={watchParty} />

        <div className="p-3 border-b border-[#E4E4E7]">
          <h3 className="text-sm font-bold text-[#09090B]">Room Chat</h3>
        </div>

        <ScrollArea className="flex-1 scrollbar-hide" ref={scrollRef}>
          <div className="p-3 space-y-2">
            {messages.length === 0 && (
              <p className="text-xs text-[#71717A] text-center py-4">No messages yet. Say hi!</p>
            )}
            {messages.map((msg) => {
              const isOwn = msg.user_id === user.id;
              return (
                <div key={msg.id} className={`p-2 border-l-2 ${
                  isOwn ? 'bg-[#FAFAFA] border-[#0047FF]' : 'bg-white border-transparent'
                }`}>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={msg.avatar} />
                      <AvatarFallback>{msg.username[0]}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-medium text-[#09090B]">{msg.username}</span>
                  </div>
                  <p className="text-sm text-[#09090B] mt-1 ml-8">{msg.content}</p>
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="p-3 border-t border-[#E4E4E7]">
          <div className="mb-2">
            <ChatTools inputMessage={inputMessage} setInputMessage={setInputMessage} onRecordingChange={setIsDictating} testIdPrefix="room-" />
          </div>
          {themedEmojis.length > 0 && (
            <div
              className="flex flex-wrap gap-1 mb-2"
              data-testid="room-themed-emoji-bar"
            >
              {themedEmojis.slice(0, 8).map((emoji, idx) => (
                <button
                  key={`${emoji}-${idx}`}
                  type="button"
                  onClick={() => setInputMessage((prev) => (prev || '') + emoji)}
                  className="text-lg hover:bg-[#F4F4F5] rounded px-1.5 py-0.5 transition-colors"
                  data-testid={`themed-emoji-${idx}`}
                  aria-label={`Insert ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChat()}
              placeholder={isDictating ? 'Listening...' : 'Type a message...'}
              className="border-[#E4E4E7] text-sm"
              data-testid="room-chat-input"
            />
            <Button 
              onClick={sendChat} 
              disabled={!inputMessage.trim()}
              size="icon"
              className="bg-[#0047FF] hover:bg-[#0037CC] text-white"
              data-testid="room-chat-send"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
